import React, { createElement, useEffect, useState } from "react";
import { PrimaryButton } from "@components/UI/Buttons/PrimaryButton/PrimaryButton";
import { useWindowSize } from "@src/hooks/useWindowsSize";
import { SeoTargets } from "@src/services/SeoTargets";
import { numberWithSpaces } from "@src/utils/formatUtils";
import { AnalyticsBanner } from "@entities/analytics";
import { AdBlock } from "@shared/ui/AdBlock";
import { AD_BLOCK_1_ID, AD_BLOCK_2_ID, AD_BLOCK_3_ID } from "@shared/ui/AdBlock/constants";
import { ImageStaticAds } from "@shared/ui/ImageStaticAds";
import CatalogCard from "../catalog-card/catalog-card";
import { Sort, TimeSort } from "../catalog-utils";
import VerticalCatalogCard from "../vertical-catalog-card/vertical-catalog-card";
import { STATIC_BANNER_POSITION, UREMONT_BANNER } from "./constants";
import LoadingData from "./loading-data";
import NotFoundOffers from "./no-data-found";
import Sorting from "./sorting";
import { CatalogListProps } from "./types";
import classes from "./catalog-list.module.scss";

const CatalogList = ({
    title = "Последние предложения",
    forceSortShow,
    data,
    loadMore,
    isDataLoading,
    page,
    onFilterChanged,
    hideShowMore = false,
    hideSort = false,
    allParamsSort = Sort.Popular,
    timeSort = TimeSort.Week,
    onTimeSortChanged,
    onParamsSortChanged,
    showResetFiltersWhenNoData = true,
    showLoadMore = true,
    total,
    defaultTimeSort = TimeSort.AllTime,
    adBlockPositions,
    headerTag = "h1"
}: CatalogListProps) => {
    const size = useWindowSize();
    const [currentHoveredCard, setCurrentHoveredCard] = useState(-1);
    const [isVertical, setIsVertical] = useState(true);
    const isOffersVisible = page !== 1 || !isDataLoading;
    const totalStr = numberWithSpaces(total || 0);

    const showMoreClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        SeoTargets.showMoreEvent();
        await loadMore();
    };

    const adBlockIds = [AD_BLOCK_1_ID, AD_BLOCK_2_ID, AD_BLOCK_3_ID];

    const [selectWidth, setSelectWidth] = useState<number | string>(200);

    useEffect(() => {
        if (size.width) {
            if (forceSortShow) {
                if (size.width < 665) {
                    setSelectWidth("100%");
                }
            } else {
                if (size.width < 550) {
                    setSelectWidth(150);
                }
            }
            if (size.width < 1000) {
                setIsVertical(true);
            }
        }
    }, [size.width]);

    return (
        <div className={classes.CatalogList} id="catalog-list">
            <div className={`${classes.Header} ${forceSortShow ? classes.ForcedToShow : null}`}>
                <div className={classes.HeaderCont}>
                    {createElement(
                        headerTag,
                        { className: classes.HeaderTitle },
                        <>
                            {title}{" "}
                            {total && !isDataLoading ? (
                                <span className={classes.Total}>{totalStr ? totalStr : null}</span>
                            ) : null}
                        </>
                    )}
                </div>

                <div className={`${classes.HeaderMenu}`}>
                    {!hideSort && (
                        <Sorting
                            selectWidth={selectWidth}
                            allParamsSort={allParamsSort}
                            timeSort={timeSort}
                            defaultTimeSort={defaultTimeSort}
                            isVertical={isVertical}
                            onParamsSortChanged={onParamsSortChanged}
                            onTimeSortChanged={onTimeSortChanged}
                            onFilterChanged={onFilterChanged}
                            setIsVertical={setIsVertical}
                        />
                    )}
                </div>
            </div>
            <AnalyticsBanner />
            <ul
                className={`${classes.CardList} ${
                    isVertical ? classes.Vertical : classes.Horizontal
                }`}
            >
                {isOffersVisible &&
                    Array.isArray(data) &&
                    data.map((item, index) => {
                        const isLastItem = index === data.length - 1;
                        const isNeighborBelow = index === currentHoveredCard - 1;
                        const showDivider = !isLastItem && !isNeighborBelow;
                        const adBlockPositionIndex = (adBlockPositions || []).indexOf(index);
                        const isAdBlockPosition = adBlockPositionIndex !== -1;

                        return (
                            <React.Fragment key={item.id}>
                                <li>
                                    {isVertical ? (
                                        <VerticalCatalogCard data={item} />
                                    ) : (
                                        <CatalogCard
                                            data={item}
                                            showDivider={showDivider}
                                            setIsHovered={(isHovered) =>
                                                setCurrentHoveredCard(isHovered ? index : -1)
                                            }
                                        />
                                    )}
                                </li>
                                {isVertical && isAdBlockPosition && (
                                    <li key={`adblock-${index}`}>
                                        {title === "Последние предложения" &&
                                        adBlockPositionIndex === STATIC_BANNER_POSITION ? (
                                            <ImageStaticAds
                                                src={UREMONT_BANNER.SRC}
                                                alt={UREMONT_BANNER.ALT}
                                                href={UREMONT_BANNER.LINK}
                                                title={UREMONT_BANNER.TITLE}
                                            />
                                        ) : (
                                            <AdBlock blockId={adBlockIds[adBlockPositionIndex]} />
                                        )}
                                    </li>
                                )}
                            </React.Fragment>
                        );
                    })}
                {isOffersVisible && !isDataLoading && data?.length === 0 && (
                    <NotFoundOffers
                        showResetFiltersWhenNoData={showResetFiltersWhenNoData}
                        onFilterChanged={onFilterChanged}
                    />
                )}
            </ul>

            {isDataLoading && <LoadingData />}

            {isOffersVisible && !hideShowMore && data?.length !== 0 && showLoadMore && (
                <PrimaryButton
                    onClick={showMoreClick}
                    isLoading={isDataLoading}
                    className={classes.PrimaryButton}
                    content={<>Показать еще</>}
                />
            )}
        </div>
    );
};
export default CatalogList;